// Autenticación independiente (JWT contra nuestra API). Expone la MISMA
// superficie que usaba Firebase Auth en el panel (onAuthStateChanged,
// signInWithEmailAndPassword, signOut, currentUser) para no reescribir la UI.
import { api, setToken, getToken, TENANT } from './api';

export type Rol = 'admin' | 'abogada' | 'abogado_asociado' | 'captadora' | 'captadora_general' | 'procuradora' | 'abogado_externo';

export interface Perfil {
  uid: string;
  email: string;
  nombre: string;
  rol: Rol;
  activo: boolean;
  estudio: string; // nombre del estudio (tenant) para la marca dinámica
  emailVerificado: boolean;
  /** El estudio contrató el panel de gestión. Falso en el que solo tiene
   *  Chatbularis: al entrar se va directo al redactor. Ausente en perfiles
   *  guardados antes de que existiera, que se leen como "con panel". */
  conPanel?: boolean;
  /** Módulos que la consola prendió para el estudio (hoy: "contabilidad"). */
  modulos?: string[];
}

const PROFILE_KEY = 'gestion_profile';

// Mapea el rol del backend (MAYÚSCULAS) al que usa el panel (minúsculas).
//
// ADMIN_NO_ABOGADO entra como 'admin' a propósito: administra el estudio
// exactamente igual —no hay una sola pantalla que uno vea y el otro no— y su
// única diferencia (no puede ser el abogado responsable de un caso) no se
// resuelve acá, sino en las listas de abogados, que llegan del servidor con el
// rol de verdad. Traducirlo evita tener que agregarlo a los casi cincuenta
// `rol === 'admin'` repartidos por el panel, donde olvidar uno le esconde un
// botón al usuario sin que nadie se entere.
const rolAPanel = (r: string): Rol =>
  r.toUpperCase() === 'ADMIN_NO_ABOGADO' ? 'admin' : (r.toLowerCase() as Rol);

interface AuthUser {
  uid: string;
  email: string;
  getIdToken: () => Promise<string>;
}

let perfil: Perfil | null = leerPerfil();
const listeners = new Set<(u: AuthUser | null) => void>();

function leerPerfil(): Perfil | null {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (raw && getToken()) return JSON.parse(raw);
  } catch {}
  return null;
}

function guardarPerfil(p: Perfil | null) {
  perfil = p;
  if (p) localStorage.setItem(PROFILE_KEY, JSON.stringify(p));
  else localStorage.removeItem(PROFILE_KEY);
  const u = toAuthUser();
  listeners.forEach((cb) => cb(u));
}

// El cliente API avisa cuando el servidor rechaza la sesión (token caducado o
// cuenta desvinculada) y ya borró el token. Falta soltar el perfil guardado:
// mientras siga ahí, el panel cree que hay alguien dentro y dibuja la pantalla
// completa con datos viejos que no se pueden guardar. Al limpiarlo se notifica
// a onAuthStateChanged y la interfaz vuelve sola al acceso.
if (typeof window !== 'undefined') {
  window.addEventListener('tabularis:sesion', () => {
    if (perfil) guardarPerfil(null);
  });
}

function toAuthUser(): AuthUser | null {
  if (!perfil) return null;
  return {
    uid: perfil.uid,
    email: perfil.email,
    getIdToken: async () => getToken() || '',
  };
}

// Objeto `auth` con la superficie mínima que consume el panel.
export const auth = {
  get currentUser(): AuthUser | null {
    return toAuthUser();
  },
};

// Devuelve el perfil del usuario actual (rol, nombre, activo).
export function perfilActual(): Perfil | null {
  return perfil;
}

export function onAuthStateChanged(_auth: unknown, cb: (u: AuthUser | null) => void) {
  listeners.add(cb);
  // Notifica el estado actual de inmediato (como Firebase).
  Promise.resolve().then(() => cb(toAuthUser()));
  return () => { listeners.delete(cb); };
}

interface RespuestaIngreso {
  token: string;
  usuario: { id: string; nombre: string; rol: string; emailVerificado?: boolean; email?: string };
  tenant?: { nombre: string; subdominio: string; conPanel?: boolean; modulos?: string[] };
}

export async function signInWithEmailAndPassword(_auth: unknown, email: string, password: string) {
  const res = await api<RespuestaIngreso>('/auth/login', { method: 'POST', auth: false, body: { subdominio: TENANT, email, password } });
  return guardarIngreso(res, email);
}

/** Canjea el pase con que la portada manda a la persona a su estudio (entró
 *  allá con correo y contraseña, sin decir el estudio). Responde y falla igual
 *  que el ingreso con contraseña: el bloqueo por vigencia sale por el mismo
 *  evento y el panel muestra la misma pantalla. */
export async function entrarConPase(pase: string) {
  const res = await api<RespuestaIngreso>('/auth/traspaso', { method: 'POST', auth: false, body: { subdominio: TENANT, pase } });
  return guardarIngreso(res, res.usuario.email || '');
}

function guardarIngreso(res: RespuestaIngreso, email: string) {
  setToken(res.token);
  guardarPerfil({
    uid: res.usuario.id,
    email,
    nombre: res.usuario.nombre,
    rol: rolAPanel(res.usuario.rol),
    activo: true,
    estudio: res.tenant?.nombre || TENANT,
    emailVerificado: res.usuario.emailVerificado ?? true,
    conPanel: res.tenant?.conPanel !== false,
    modulos: res.tenant?.modulos ?? [],
  });
  return { user: toAuthUser() };
}

// Marca el correo del perfil actual como verificado (tras verificar).
export function marcarEmailVerificado() {
  if (perfil) guardarPerfil({ ...perfil, emailVerificado: true });
}

/** Actualiza el rol del perfil guardado. Hace falta cuando un admin se cambia
 *  el rol a sí mismo: el servidor ya manda por rol real en cada petición, pero
 *  el perfil del navegador se escribió al entrar y seguiría dibujando un menú
 *  que la API ya no autoriza. */
export function marcarRol(rolNuevo: string) {
  if (perfil) guardarPerfil({ ...perfil, rol: rolAPanel(rolNuevo) });
}

/** Pone al día el perfil guardado con lo que dice la base. Cuando el estudio
 *  le cambia el rol a alguien (por ejemplo, de captador a captador general),
 *  el servidor lo aplica de inmediato, pero el panel seguía con el rol del
 *  inicio de sesión y le escondía lo que ya podía hacer hasta volver a entrar.
 *  Si falla, se queda con el perfil guardado: no vale la pena cortar la
 *  entrada por esto. */
export async function refrescarPerfil() {
  if (!perfil || !getToken()) return;
  try {
    const yo = await api<{ nombre: string; rol: string; emailVerificado: boolean; modulos?: string[] }>('/auth/yo');
    if (!perfil) return;
    const rol = rolAPanel(yo.rol);
    const modulos = yo.modulos ?? [];
    const mismosModulos = modulos.join(',') === (perfil.modulos ?? []).join(',');
    if (rol !== perfil.rol || yo.nombre !== perfil.nombre || yo.emailVerificado !== perfil.emailVerificado || !mismosModulos) {
      guardarPerfil({ ...perfil, rol, nombre: yo.nombre, emailVerificado: yo.emailVerificado, modulos });
    }
  } catch {}
}

/** El estudio que solo tenía Chatbularis se sumó el panel. El perfil guardado
 *  al entrar decía "sin panel" y mandaría de vuelta al asistente. */
export function marcarConPanel() {
  if (perfil) guardarPerfil({ ...perfil, conPanel: true });
}

// ── Cuenta (recuperación / verificación) — usan el tenant del subdominio ──
export async function solicitarReset(email: string) {
  await api('/auth/forgot', { method: 'POST', auth: false, body: { subdominio: TENANT, email } });
}
/** Fija la contraseña con el enlace del correo. Devuelve el correo de la
 *  cuenta, para entrar de inmediato con la contraseña recién creada. */
export async function aplicarReset(token: string, password: string): Promise<string | undefined> {
  const r = await api<{ ok: boolean; email?: string }>('/auth/reset', { method: 'POST', auth: false, body: { subdominio: TENANT, token, password } });
  return r?.email;
}
export async function verificarEmail(token: string) {
  await api('/auth/verify-email', { method: 'POST', auth: false, body: { subdominio: TENANT, token } });
}
export async function reenviarVerificacion() {
  await api('/auth/resend-verification', { method: 'POST', body: {} });
}

export async function signOut(_auth?: unknown) {
  setToken(null);
  guardarPerfil(null);
}
